import { cn } from '@/lib/utils';
import type { QRConfig, QRGradient } from '@/types/qrCode';
import { FG_PRESETS, BG_PRESETS, DOT_STYLES } from '@/components/qr/qrPresets';

interface QRColorDotControlsProps {
  fgColor: string;
  bgColor: string;
  fgGradient: QRGradient | undefined;
  bgGradient: QRGradient | undefined;
  dotStyle: NonNullable<QRConfig['dotStyle']>;
  showFgGradient: boolean;
  showBgGradient: boolean;
  setShowFgGradient: (v: boolean) => void;
  setShowBgGradient: (v: boolean) => void;
  updateConfig: (patch: Partial<QRConfig>) => void;
  defaultGradient: () => QRGradient;
}

export function QRColorDotControls({
  fgColor,
  bgColor,
  fgGradient,
  bgGradient,
  dotStyle,
  showFgGradient,
  showBgGradient,
  setShowFgGradient,
  setShowBgGradient,
  updateConfig,
  defaultGradient,
}: QRColorDotControlsProps) {
  return (
    <>
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">
            Foreground
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            {FG_PRESETS.map((p) => (
              <button
                key={p.color}
                type="button"
                onClick={() => updateConfig({ fgColor: p.color, fgGradient: undefined })}
                title={p.label}
                className={cn(
                  'w-7 h-7 rounded-full border-2 transition-all shrink-0',
                  fgColor === p.color && !fgGradient ? 'border-brand scale-110' : 'border-white/20 hover:border-white/40'
                )}
                style={{ backgroundColor: p.color }}
              />
            ))}
            <label className="relative w-7 h-7 shrink-0">
              <input
                type="color"
                value={fgColor}
                onChange={(e) => updateConfig({ fgColor: e.target.value, fgGradient: undefined })}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div
                className={cn(
                  'w-7 h-7 rounded-full border-2 border-dashed border-white/30 hover:border-white/50 transition-colors',
                  !FG_PRESETS.find((p) => p.color === fgColor) && !fgGradient ? 'border-brand' : ''
                )}
                style={{ backgroundColor: fgColor }}
              />
            </label>
            <button
              type="button"
              onClick={() => {
                if (showFgGradient) {
                  setShowFgGradient(false);
                  updateConfig({ fgGradient: undefined });
                } else {
                  setShowFgGradient(true);
                  updateConfig({ fgGradient: fgGradient || defaultGradient() });
                }
              }}
              className={cn(
                'text-[10px] font-semibold px-2 py-1 rounded transition-colors',
                showFgGradient
                  ? 'bg-brand/15 text-brand'
                  : 'bg-white/5 text-[var(--text-secondary)] hover:text-[var(--text-main)]'
              )}
            >
              Gradient
            </button>
          </div>

          {showFgGradient && fgGradient && (
            <div className="space-y-2 pl-1">
              <div className="flex items-center gap-2">
                <label className="relative w-8 h-8 shrink-0 rounded-md overflow-hidden border border-white/20 cursor-pointer">
                  <input
                    type="color"
                    value={fgGradient.colorStops[0]?.color || '#8FB560'}
                    onChange={(e) =>
                      updateConfig({
                        fgGradient: {
                          ...fgGradient,
                          colorStops: [
                            { offset: 0, color: e.target.value },
                            fgGradient.colorStops[1] || { offset: 1, color: '#FFFFFF' },
                          ],
                        },
                      })
                    }
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="w-full h-full" style={{ backgroundColor: fgGradient.colorStops[0]?.color || '#8FB560' }} />
                </label>
                <span className="text-[10px] text-[var(--text-secondary)]">→</span>
                <label className="relative w-8 h-8 shrink-0 rounded-md overflow-hidden border border-white/20 cursor-pointer">
                  <input
                    type="color"
                    value={fgGradient.colorStops[1]?.color || '#FFFFFF'}
                    onChange={(e) =>
                      updateConfig({
                        fgGradient: {
                          ...fgGradient,
                          colorStops: [
                            fgGradient.colorStops[0] || { offset: 0, color: '#8FB560' },
                            { offset: 1, color: e.target.value },
                          ],
                        },
                      })
                    }
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="w-full h-full" style={{ backgroundColor: fgGradient.colorStops[1]?.color || '#FFFFFF' }} />
                </label>
                <span className="text-[10px] text-[var(--text-secondary)] ml-1 whitespace-nowrap">
                  {fgGradient.colorStops[0]?.color || '#8FB560'} → {fgGradient.colorStops[1]?.color || '#FFFFFF'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--text-secondary)] w-10">Angle</span>
                <input
                  type="range"
                  min={0}
                  max={360}
                  value={fgGradient.rotation || 0}
                  onChange={(e) =>
                    updateConfig({
                      fgGradient: { ...fgGradient, rotation: Number(e.target.value) },
                    })
                  }
                  className="flex-1 h-1 accent-brand"
                />
                <span className="text-[10px] text-[var(--text-secondary)] tabular-nums w-8">{fgGradient.rotation || 0}°</span>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">Background</span>
          <div className="flex items-center gap-2 flex-wrap">
            {BG_PRESETS.map((p) => (
              <button
                key={p.color}
                type="button"
                onClick={() => updateConfig({ bgColor: p.color, bgGradient: undefined })}
                title={p.label}
                className={cn(
                  'w-7 h-7 rounded-full border-2 transition-all shrink-0',
                  bgColor === p.color && !bgGradient ? 'border-brand scale-110' : 'border-white/20 hover:border-white/40'
                )}
                style={{ backgroundColor: p.color }}
              />
            ))}
            <label className="relative w-7 h-7 shrink-0">
              <input
                type="color"
                value={bgColor}
                onChange={(e) => updateConfig({ bgColor: e.target.value, bgGradient: undefined })}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div
                className={cn(
                  'w-7 h-7 rounded-full border-2 border-dashed border-white/30 hover:border-white/50 transition-colors',
                  !BG_PRESETS.find((p) => p.color === bgColor) && !bgGradient ? 'border-brand' : ''
                )}
                style={{ backgroundColor: bgColor }}
              />
            </label>
            <button
              type="button"
              onClick={() => {
                if (showBgGradient) {
                  setShowBgGradient(false);
                  updateConfig({ bgGradient: undefined });
                } else {
                  setShowBgGradient(true);
                  updateConfig({
                    bgGradient: bgGradient || {
                      type: 'linear',
                      rotation: 0,
                      colorStops: [
                        { offset: 0, color: '#111111' },
                        { offset: 1, color: '#333333' },
                      ],
                    },
                  });
                }
              }}
              className={cn(
                'text-[10px] font-semibold px-2 py-1 rounded transition-colors',
                showBgGradient
                  ? 'bg-brand/15 text-brand'
                  : 'bg-white/5 text-[var(--text-secondary)] hover:text-[var(--text-main)]'
              )}
            >
              Gradient
            </button>
          </div>

          {showBgGradient && bgGradient && (
            <div className="space-y-2 pl-1">
              <div className="flex items-center gap-2">
                <label className="relative w-8 h-8 shrink-0 rounded-md overflow-hidden border border-white/20 cursor-pointer">
                  <input
                    type="color"
                    value={bgGradient.colorStops[0]?.color || '#111111'}
                    onChange={(e) =>
                      updateConfig({
                        bgGradient: {
                          ...bgGradient,
                          colorStops: [
                            { offset: 0, color: e.target.value },
                            bgGradient.colorStops[1] || { offset: 1, color: '#333333' },
                          ],
                        },
                      })
                    }
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="w-full h-full" style={{ backgroundColor: bgGradient.colorStops[0]?.color || '#111111' }} />
                </label>
                <span className="text-[10px] text-[var(--text-secondary)]">→</span>
                <label className="relative w-8 h-8 shrink-0 rounded-md overflow-hidden border border-white/20 cursor-pointer">
                  <input
                    type="color"
                    value={bgGradient.colorStops[1]?.color || '#333333'}
                    onChange={(e) =>
                      updateConfig({
                        bgGradient: {
                          ...bgGradient,
                          colorStops: [
                            bgGradient.colorStops[0] || { offset: 0, color: '#111111' },
                            { offset: 1, color: e.target.value },
                          ],
                        },
                      })
                    }
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="w-full h-full" style={{ backgroundColor: bgGradient.colorStops[1]?.color || '#333333' }} />
                </label>
                <span className="text-[10px] text-[var(--text-secondary)] ml-1 whitespace-nowrap">
                  {bgGradient.colorStops[0]?.color || '#111111'} → {bgGradient.colorStops[1]?.color || '#333333'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--text-secondary)] w-10">Angle</span>
                <input
                  type="range"
                  min={0}
                  max={360}
                  value={bgGradient.rotation || 0}
                  onChange={(e) =>
                    updateConfig({
                      bgGradient: { ...bgGradient, rotation: Number(e.target.value) },
                    })
                  }
                  className="flex-1 h-1 accent-brand"
                />
                <span className="text-[10px] text-[var(--text-secondary)] tabular-nums w-8">{bgGradient.rotation || 0}°</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-full space-y-2">
        <span className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">Dot Style</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {DOT_STYLES.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => updateConfig({ dotStyle: s.value })}
              className={cn(
                'text-[10px] font-medium px-2.5 py-1.5 rounded transition-colors',
                dotStyle === s.value
                  ? 'bg-brand/15 text-brand'
                  : 'bg-white/5 text-[var(--text-secondary)] hover:text-[var(--text-main)] hover:bg-white/10'
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
